import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * Three-state theme model:
 *   - 'light' / 'dark' → explicit user choice, persists across sessions
 *   - 'system'         → follows the OS preference, updates live as it changes
 *
 * `resolvedTheme` ('light' | 'dark') is the value actually applied to <html>.
 * Components that just need to know "are we dark right now?" should read
 * `resolvedTheme`. Components that render a setting picker should read `theme`.
 */
const STORAGE_KEY = 'nubod.theme';

const ThemeContext = createContext(null);

const isBrowser = typeof window !== 'undefined';

function getSystemTheme() {
  if (!isBrowser || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function readStoredTheme() {
  if (!isBrowser) return 'light';
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  // Phase 0 default: 'light'. Existing pages haven't been migrated to have
  // proper dark variants yet, so defaulting to 'system' would surprise users
  // on dark-mode-preferring devices. We can flip this default to 'system'
  // once enough pages are migrated.
  return 'light';
}

function applyDarkClass(resolved) {
  if (!isBrowser) return;
  const root = document.documentElement;
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  // Helps native form controls (scrollbars, date pickers) render in the right
  // shade in browsers that respect this hint.
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStoredTheme());
  const [systemTheme, setSystemTheme] = useState(() => getSystemTheme());

  // Resolved value = explicit choice when set, otherwise the live system value.
  const resolvedTheme = theme === 'system' ? systemTheme : theme;

  // Keep <html class="dark"> in sync with the resolved theme.
  useEffect(() => {
    applyDarkClass(resolvedTheme);
  }, [resolvedTheme]);

  // Live-update if the OS theme changes while the user is on 'system'.
  useEffect(() => {
    if (!isBrowser || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setSystemTheme(e.matches ? 'dark' : 'light');
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange); // older Safari
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  const setTheme = useCallback((next) => {
    if (next !== 'light' && next !== 'dark' && next !== 'system') return;
    setThemeState(next);
    if (isBrowser) {
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch (_e) {
        /* Storage may be disabled (private mode); not fatal. */
      }
    }
  }, []);

  /** Cycle: light → dark → system → light. Convenient for an icon-only toggle. */
  const cycleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light');
  }, [theme, setTheme]);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme, cycleTheme }),
    [theme, resolvedTheme, setTheme, cycleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Don't crash — but this means the provider isn't mounted. Loud-fail in
    // dev so we catch missing providers; soft-fall in production.
    if (process.env.NODE_ENV !== 'production') {
      console.warn('useTheme() called outside <ThemeProvider>.');
    }
    return {
      theme: 'light',
      resolvedTheme: 'light',
      setTheme: () => {},
      cycleTheme: () => {},
    };
  }
  return ctx;
}
